import { useState, useEffect } from 'react'
import { Card, Button, Input, Badge } from '../../components/ui'
import { Search, Loader2, MoreVertical, Building, Mail, Calendar, Shield } from 'lucide-react'
import { supabase } from '../../lib/supabase'

export const PlatformClients = () => {
    const [clients, setClients] = useState([])
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')

    useEffect(() => {
        fetchClients()
    }, [])

    const fetchClients = async () => {
        try {
            const { data, error } = await supabase
                .from('tenants')
                .select('*')
                .order('created_at', { ascending: false })

            if (error) throw error
            setClients(data)
        } catch (error) {
            console.error('Error fetching clients:', error)
        } finally {
            setLoading(false)
        }
    }

    const filteredClients = clients.filter(client =>
        client.name?.toLowerCase().includes(search.toLowerCase()) ||
        client.id?.toLowerCase().includes(search.toLowerCase())
    )

    return (
        <div className="p-8 max-w-7xl mx-auto bg-gray-50 min-h-screen">
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                        <UsersIcon />
                        Klanten Beheer
                    </h1>
                    <p className="text-gray-500">Overzicht van alle geregistreerde organisaties en gebruikers.</p>
                </div>
                <div className="flex gap-3">
                    <Button variant="outline">Exporteer CSV</Button>
                    <Button>Nieuwe Klant</Button>
                </div>
            </div>

            <Card className="p-4 mb-6">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <Input
                        placeholder="Zoek op naam of ID..."
                        className="pl-10"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </Card>

            {loading ? (
                <div className="flex justify-center py-12">
                    <Loader2 className="animate-spin text-orange-500" size={40} />
                </div>
            ) : (
                <div className="grid gap-4">
                    {filteredClients.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">Geen klanten gevonden.</div>
                    ) : (
                        filteredClients.map(client => (
                            <div key={client.id} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between hover:shadow-md transition-shadow">
                                <div className="flex items-center gap-6">
                                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                                        {client.name?.substring(0, 2).toUpperCase()}
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
                                            {client.name}
                                            {client.plan === 'premium' && <Badge variant="warning" size="sm">PRO</Badge>}
                                        </h3>
                                        <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                                            <span className="flex items-center gap-1"><Shield size={14} /> {client.id}</span>
                                            <span className="flex items-center gap-1"><Calendar size={14} /> {new Date(client.created_at).toLocaleDateString()}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-4">
                                    <div className="text-right mr-4">
                                        <div className="text-sm font-bold text-gray-900">{client.plan || 'Free'}</div>
                                        <div className="text-xs text-gray-400">Huidig Plan</div>
                                    </div>
                                    <Button variant="ghost" size="icon">
                                        <MoreVertical size={20} className="text-gray-400" />
                                    </Button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    )
}

const UsersIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
)
